import { laagOf } from '@eformulieren/engine'
import type { FieldId, Person, Projection, VerwijzingOutcome, ViewMode } from '@eformulieren/model'
import { type PresenceSource, type SectionConnection, ownPresence, prefillMessage } from '@eformulieren/renderer'
import { carryOverAcrossSeam } from '@eformulieren/renderer/document'
import { HocuspocusProvider, HocuspocusProviderWebsocket, WebSocketStatus } from '@hocuspocus/provider'
import * as Y from 'yjs'
import { accessChangeOf, isEndOfAccess, rightChangeOf } from './access.js'
import type { FormContext } from './formContext.js'
import { isNewGenerationClose, newGenerationOf } from './generation.js'
import { isOriginRejectedClose } from './originRejected.js'
import { BACKOFF_STABLE_MS, createBackoff, isRateLimitedClose } from './rateLimited.js'
import { SessionExpiredError, createUnsavedTracker, isSessionExpiredClose } from './session.js'

/**
 * De verbinding van de formulierpagina met de samenwerklaag: één socket, een document per sectie
 * (R-72, ontwerp 9.2). Tot deze opsplitsing stond dit in `FormPage.tsx`, dat de verbinding opent en
 * opruimt.
 */

/**
 * De websocket van de samenwerklaag, op dezelfde oorsprong als deze pagina.
 *
 * Afgeleid en niet opgegeven: de pod serveert deze pagina, dus hij is de oorsprong. Een
 * adres dat de client mag kiezen zou de herkomstcontrole op de socket zinloos maken.
 */
function wsAddress(): string {
  const schema = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${schema}//${window.location.host}/samenwerken`
}

export interface Connection {
  readonly projectie: Projection
  /** De gedaante voor deze instantie en deze kijker: expliciet, of uit de instantie (`gedaante.ts`). */
  readonly modus: ViewMode
  /** De opgeloste verwijzingen uit het dossier, van de pod (R-54, R-61, R-63). */
  readonly verwijzingen: Readonly<Record<string, VerwijzingOutcome>>
  readonly documenten: ReadonlyMap<string, Y.Doc>
  /**
   * De provider per sectie, voor de presence en de cursor (R-72).
   *
   * De schil krijgt hier de provider zelf en zijn awareness: de cursor-extensie van tiptap heeft
   * de provider nodig, de presence-balk leest de awareness. Dit is wat een gastheer doet, en
   * daarom staat het in het harnas en niet in de schil.
   */
  readonly verbindingen: ReadonlyMap<string, { provider: unknown; awareness?: PresenceSource }>
  readonly identiteit: Person
  /** Wacht tot alles wat er lokaal staat door de pod is aangenomen. */
  save(): Promise<void>
  /**
   * De sessie is verlopen (`session.ts`): de socket gaat dicht en verbindt niet opnieuw, een
   * wachtende en elke volgende `save()` faalt met {@link SessionExpiredError}. De documenten blijven,
   * en de providers ook, zodat de editors aan hun document blijven hangen en het formulier in beeld
   * blijft zoals het was.
   */
  expire(): void
  /** Sluit de sockets; nodig omdat React in StrictMode elk effect twee keer draait. */
  dispose(): void
  /**
   * Deze sectie telt niet meer mee bij `save()`: haar toegang is weg of verlaagd naar lezen (besluit 9).
   * Wat er lokaal nog stond, neemt de pod nooit meer aan, en wachten daarop zou indienen en een sprong
   * eeuwig bezig laten staan. Met `sluiten` gaat de provider ook dicht, zodat hij niet opnieuw verbindt.
   */
  release(sectionId: string, sluiten: boolean): void
  /** Of in deze sectie nog iets lokaal staat dat de pod niet heeft bevestigd. */
  unsavedIn(sectionId: string): boolean
}

/** Wat de verbinding de pagina over een generatienaad zegt (`onGeneration`). */
export type GenerationEvent =
  | { readonly soort: 'wisselt' }
  | {
      readonly soort: 'gewisseld'
      /** Het document van vóór de naad, om wat niet terug kon als tekst te kunnen tonen. */
      readonly oud: Y.Doc
      readonly teruggezet: readonly FieldId[]
      readonly nietTeruggezet: readonly FieldId[]
    }

export function connect(
  context: FormContext,
  projection: Projection,
  verwijzingen: Readonly<Record<string, VerwijzingOutcome>>,
  mode: ViewMode,
  /** De pod sloot een verbinding met `geen-recht`: de toegang is beëindigd (`access.ts`). */
  onAccessEnded: () => void,
  /**
   * De sessie is verlopen, of de handdruk werd geweigerd (`session.ts`). Bij een sluiting met
   * `token-verlopen` zeker; bij een geweigerde handdruk zoekt de pagina het uit, want die kan ook een
   * ingetrokken recht zijn.
   */
  onSession: (certain: boolean) => void,
  /** Of er nu een eigen wijziging is die de pod niet heeft bevestigd (`createUnsavedTracker`). */
  onUnsaved: (unsaved: boolean) => void,
  /**
   * De pod sloot het document van één sectie met `toegang-gewijzigd` (4006): een deel van de toegang is
   * weg, de rest werkt door (besluit 9, G-02). Zonder deze functie telt het als einde van de toegang.
   */
  onAccessChanged?: (sectionId: string) => void,
  /** De pod zette deze sectie op een open verbinding naar lezen (`{soort:'recht'}`, besluit 9). */
  onDowngrade?: (sectionId: string) => void,
  /**
   * De pod weigerde de socket omdat deze pagina van een oorsprong komt die niet in zijn lijst staat
   * (`herkomst-niet-toegestaan`, `originRejected.ts`). Eindstand: de socket verbindt niet opnieuw.
   */
  onOriginRejected?: () => void,
  /**
   * Deze sectie wacht, of wacht niet meer, op de grens per gebruiker (`te-veel-verzoeken` op één
   * document, `rateLimited.ts`). De pagina zegt dat, en de verbinding probeert het intussen opnieuw.
   */
  onWaiting?: (sectionId: string, waiting: boolean) => void,
  /**
   * Deze sectie wisselt van generatie (eisenboek 12.50, `generation.ts`): `wisselt` zodra de pod het document
   * sloot, `gewisseld` als het nieuwe document er is, met het oude erbij en de velden waarvan de eigen invoer
   * wel of niet in het nieuwe staat. Daartussen toont de pagina het oude document alleen-lezen.
   */
  onGeneration?: (sectionId: string, event: GenerationEvent) => void,
): Connection {
  // Eén socket voor de hele pagina en een document per sectie: een client houdt
  // meerdere documenten over één verbinding open (9.2, nulmeting M3.14).
  const socket = new HocuspocusProviderWebsocket({ url: wsAddress() })
  const providers: HocuspocusProvider[] = []
  const perSection = new Map<string, HocuspocusProvider>()
  /** De providers die `save()` niet meer afwacht (`release`). */
  const released = new Set<HocuspocusProvider>()
  const documents = new Map<string, Y.Doc>()
  const connections = new Map<string, SectionConnection>()
  let left = false
  let expired = false
  const tracker = createUnsavedTracker(onUnsaved)
  /** De `save()`-aanroepen die nog wachten; bij het verlopen falen ze, want er komt niets meer aan. */
  const waiting = new Set<(error: Error) => void>()
  // Een herverbinding die toch nog opkomt (de bibliotheek plant er een bij het sluiten) gaat meteen
  // weer dicht: elke handdruk zonder cookie kost een verzoek van de begrenzing per adres.
  socket.on('open', () => {
    if (expired) socket.disconnect()
  })
  /**
   * Een lege emmer: de pod sloot de socket met `te-veel-verzoeken` (`rateLimited.ts`). Het formulier
   * blijft staan, en de pagina komt terug na een wachttijd die bij elke volgende sluiting met die reden
   * groeit. Op `disconnect` en niet op `close`: de bibliotheek besluit in hetzelfde `onClose` na dit
   * event of ze na een seconde opnieuw verbindt, en zonder `shouldConnect` doet ze dat niet.
   */
  const backoff = createBackoff()
  let retry: ReturnType<typeof setTimeout> | undefined
  let stable: ReturnType<typeof setTimeout> | undefined
  /** De nieuwe pogingen per sectie na een weigering met `te-veel-verzoeken` (hieronder, per provider). */
  const sectionRetries = new Map<string, ReturnType<typeof setTimeout>>()
  const stopSectionRetries = (): void => {
    for (const timer of sectionRetries.values()) clearTimeout(timer)
    sectionRetries.clear()
  }
  const stopTimers = (): void => {
    clearTimeout(retry)
    clearTimeout(stable)
    retry = undefined
    stable = undefined
    stopSectionRetries()
  }
  let originRejected = false
  socket.on('connect', () => {
    clearTimeout(stable)
    stable = setTimeout(() => backoff.reset(), BACKOFF_STABLE_MS)
  })
  socket.on('disconnect', (data: { event?: { code?: number; reason?: string } }) => {
    clearTimeout(stable)
    // Komt de socket terug, dan meldt elke provider zich daar zelf opnieuw aan; een geplande poging per
    // sectie zou dan een tweede aanmelding sturen.
    stopSectionRetries()
    /**
     * Een oorsprong die de pod niet toelaat (`originRejected.ts`): eindstand. Zonder `shouldConnect` uit
     * zou de bibliotheek elke seconde opnieuw verbinden en elke keer dezelfde sluiting krijgen.
     */
    if (isOriginRejectedClose(data.event)) {
      socket.shouldConnect = false
      if (!originRejected && !expired && !left) {
        originRejected = true
        onOriginRejected?.()
      }
      return
    }
    if (!isRateLimitedClose(data.event)) return
    socket.shouldConnect = false
    if (expired || left) return
    clearTimeout(retry)
    retry = setTimeout(() => {
      retry = undefined
      if (!expired && !left) void socket.connect()
    }, backoff.next())
  })

  /** Het bericht van de pod bij de laatste naad per sectie: de eindsnapshot (`generation.ts`). */
  const seams = new Map<string, Uint8Array>()
  /**
   * De secties die nu van generatie wisselen (eisenboek 12.50), met het document dat de pagina nog toont: dat
   * van vóór de naad, waaruit de eigen invoer komt. Tot de nieuwe provider zijn eerste synchronisatie had,
   * blijft het in beeld en niet een leeg document (valkuil 6).
   */
  const switching = new Map<string, { readonly oud: Y.Doc; readonly provider: HocuspocusProvider | undefined; readonly timer?: ReturnType<typeof setTimeout> }>()
  /** Een opnieuw geweigerde opening wacht, met een wachttijd die per sectie groeit. */
  const switchBackoffs = new Map<string, ReturnType<typeof createBackoff>>()
  /** Wie op `save()` wacht, kijkt opnieuw als een sectie van provider wisselt. */
  const wakers = new Set<() => void>()
  const wake = (): void => {
    for (const waker of [...wakers]) waker()
  }

  type ProjectedSection = Projection['secties'][number]
  type SectionLaag = NonNullable<ReturnType<typeof laagOf>>

  /** De provider en het document van één sectie, met alles wat de pagina erop hoort (R-72, 9.2). */
  function openSection(section: ProjectedSection, laag: SectionLaag): { doc: Y.Doc; provider: HocuspocusProvider; verbinding: SectionConnection } {
    // De opruiming van verwijderde inhoud staat uit, aan beide kanten (valkuil 1).
    const doc = new Y.Doc({ gc: false })
    const provider = new HocuspocusProvider({
      websocketProvider: socket,
      name: `${context.instantieId}/${laag}/${section.id}`,
      // Leeg en niet weggelaten: de samenwerklaag leest bij een leeg token het cookie uit de
      // handshake, en dat is dezelfde regel als bij de HTTP-verzoeken (`headersFor` in `podRequests.ts`).
      token: context.token ?? '',
      document: doc,
    })
    provider.attach()
    /**
     * Een sluiting met `geen-recht` is eindstand (R-155b, ontwerp 6.4 en 9.2): de deling is
     * ingetrokken of de toekenning is weg. Op de provider en niet op de socket, want de provider
     * hoort beide vormen: de socket die de pod met 4001 sluit, en het sluitbericht voor één
     * document, dat als code 1000 met de reden binnenkomt (`toegang.ts`).
     *
     * Een sluiting met `nieuwe-generatie` (eisenboek 12.50, `generation.ts`): dit document gaat weg en de
     * sectie haalt het opnieuw op.
     */
    provider.on('close', (data: { event?: { code?: number; reason?: string } }) => {
      if (isNewGenerationClose(data.event)) {
        switchGeneration(section, laag, provider)
        return
      }
      if (accessChangeOf(data.event) === 'gewijzigd' && onAccessChanged !== undefined) onAccessChanged(section.id)
      else if (isEndOfAccess(data.event)) onAccessEnded()
      else if (isSessionExpiredClose(data.event)) onSession(true)
    })
    provider.on('stateless', (data: { payload: string }) => {
      // De eindsnapshot komt vlak vóór de sluiting met `nieuwe-generatie` (`generation.ts`).
      const seam = newGenerationOf(data.payload)
      if (seam !== undefined) {
        if (seam.sectie === section.id) seams.set(section.id, seam.eindsnapshot)
        return
      }
      // De verlaging naar lezen komt als bericht en niet als sluiting (api-contract afwijking 9).
      const change = rightChangeOf(data.payload)
      if (change !== undefined && change.sectie === section.id) onDowngrade?.(section.id)
    })
    /**
     * De grens per gebruiker (9.2, opdracht van 1 november 2026): de pod weigert dit ene document met de
     * reden `te-veel-verzoeken` en laat de andere secties staan (`reserveOpening` in
     * `apps/runtime/src/collaboration.ts`). Geen eindstand en geen verlopen sessie: deze sectie probeert
     * het opnieuw na een wachttijd die bij elke volgende weigering groeit, en de pagina zegt intussen
     * waarom ze wacht. Tot deze opdracht kwam die weigering zonder reden binnen, ging ze naar de
     * sessiecontrole, en bleef de sectie daarna leeg zonder dat iemand het zag (valkuil 6).
     */
    const sectionBackoff = createBackoff()
    provider.on('authenticated', () => {
      sectionBackoff.reset()
      onWaiting?.(section.id, false)
    })
    // Een geweigerde handdruk sluit niets: de provider meldt het alleen. Na een slaapstand van de
    // laptop is dit de enige weg waarlangs een verlopen sessie zich laat zien.
    provider.on('authenticationFailed', (data: { reason?: string }) => {
      if (expired || left) return
      if (!isRateLimitedClose(data.reason === undefined ? {} : { reason: data.reason })) {
        onSession(false)
        return
      }
      onWaiting?.(section.id, true)
      clearTimeout(sectionRetries.get(section.id))
      sectionRetries.set(
        section.id,
        setTimeout(() => {
          sectionRetries.delete(section.id)
          if (expired || left || released.has(provider) || provider.isAuthenticated) return
          // Alleen op een open socket. Is hij dicht, dan meldt de provider zich bij het terugkomen zelf aan.
          if (socket.status !== WebSocketStatus.Connected) return
          void provider.sendToken().then(() => provider.startSync())
        }, sectionBackoff.next()),
      )
    })
    tracker.watch(doc, provider)
    /**
     * De eigen naam in de awareness, zodat de anderen hem zien.
     *
     * Uit de context, dus uit het token, en niet uit een invoerveld. De pod zet de naam toch
     * opnieuw uit het token van de verbinding en neemt alleen de staat onder de eigen clientId aan
     * (`apps/runtime/src/boundAwareness.ts`, beveiligingsronde van 1 november 2026); dit is het
     * tweede slot.
     *
     * **Behalve aan de interne kant op een document dat de externe kant altijd leest** (R-66a, B6,
     * opdracht 8): dan het merkteken van een medewerker van NWO en geen persoon. De pod herschrijft
     * het toch; dit is het tweede slot, zodat deze pagina het niet eens probeert.
     */
    provider.awareness?.setLocalStateField('user', ownPresence(context.persoon, projection.kant, laag))
    const verbinding: SectionConnection = {
      provider,
      // De voorvulling uit de querystring gaat naar de pod, die haar in zijn eigen document zet; zo
      // krijgen twee mensen die tegelijk dezelfde link openen de tekst één keer (R-37, R-72).
      prefill: (values) => provider.sendStateless(prefillMessage(values)),
      ...(provider.awareness === null || provider.awareness === undefined
        ? {}
        : { awareness: provider.awareness as unknown as PresenceSource }),
    }
    return { doc, provider, verbinding }
  }

  /**
   * Een provider weg zonder dat hij nog iets stuurt (eisenboek 12.50).
   *
   * De pod sloot dit document al. Een sluitbericht of een laatste update van deze provider zou bij de pod
   * pas aankomen ná de aanmelding van de nieuwe provider onder dezelfde naam: Hocuspocus zet berichten voor
   * een naam die nog niet is aangemeld in een wachtrij en speelt ze na de aanmelding af, en dan sloot het
   * sluitbericht de nieuwe verbinding, of bracht de update de vorige generatie alsnog binnen. Daarom eerst
   * uit de tabel van de socket, dan los (dan stuurt hij niets meer), en dan pas opruimen; en dat alles
   * vóór de nieuwe provider bestaat, want `detach` haalt de naam uit dezelfde tabel.
   */
  function retire(provider: HocuspocusProvider): void {
    socket.configuration.providerMap.delete(provider.effectiveName)
    provider.detach()
    provider.destroy()
  }

  /** De velden die deze pagina in deze sectie niet schrijft: een veld van het processysteem dat niet aanpasbaar is (R-54, R-58). */
  function lockedFields(section: ProjectedSection): ReadonlySet<string> {
    return new Set(
      section.velden.filter((field) => 'dossierveld' in field && field.dossierveld !== undefined && !field.dossierveld.aanpasbaar).map((field) => field.id),
    )
  }

  /**
   * De pod sloot dit document met `nieuwe-generatie` (ontwerp 9.2, eisenboek 12.50): na een compactie, of omdat
   * deze pagina terugkwam met een document van vóór de naad. Het document en de provider van deze sectie gaan
   * weg, een nieuw paar haalt de nieuwe generatie op, en pas na de eerste synchronisatie wisselt het formulier
   * van document: tot dan blijft het oude in beeld (valkuil 6), alleen-lezen (`onGeneration`). Daarna zet de
   * pagina de eigen invoer terug die de pod bij de naad niet had (`carryOverAcrossSeam`); wat daar niet kon,
   * krijgt de invuller om te kopiëren.
   */
  function switchGeneration(section: ProjectedSection, laag: SectionLaag, closed: HocuspocusProvider): void {
    if (left || expired || released.has(closed)) return
    const pending = switching.get(section.id)
    // Alleen de provider die nu deze sectie draagt; een sluiting van een al weggezette provider telt niet.
    if (perSection.get(section.id) !== closed && pending?.provider !== closed) return
    const oud = pending?.oud ?? documents.get(section.id)
    if (oud === undefined) return
    retire(closed)
    clearTimeout(pending?.timer)
    // Een tweede weigering op rij (de nieuwe provider kwam er weer niet in): even wachten.
    const backoff = switchBackoffs.get(section.id) ?? createBackoff()
    switchBackoffs.set(section.id, backoff)
    const delay = pending === undefined ? 0 : backoff.next()
    if (pending === undefined) onGeneration?.(section.id, { soort: 'wisselt' })
    const timer = setTimeout(() => {
      if (left || expired) return
      const opened = openSection(section, laag)
      switching.set(section.id, { oud, provider: opened.provider })
      const index = providers.indexOf(closed)
      if (index >= 0) providers[index] = opened.provider
      else providers.push(opened.provider)
      perSection.set(section.id, opened.provider)
      wake()
      const finish = (): void => {
        if (!opened.provider.isSynced || switching.get(section.id)?.provider !== opened.provider) return
        opened.provider.off('synced', finish)
        const eindsnapshot = seams.get(section.id)
        seams.delete(section.id)
        const writable = section.recht === 'bewerken' && !released.has(opened.provider)
        const outcome = writable
          ? carryOverAcrossSeam({
              sectie: section,
              oud,
              eindsnapshot,
              nieuw: opened.doc,
              herkomst: { door: context.persoon, gewijzigdOp: new Date().toISOString() },
              overslaan: lockedFields(section),
            })
          : { teruggezet: [], nietTeruggezet: [] }
        documents.set(section.id, opened.doc)
        connections.set(section.id, opened.verbinding)
        tracker.forget(oud)
        switching.delete(section.id)
        backoff.reset()
        wake()
        onGeneration?.(section.id, { soort: 'gewisseld', oud, teruggezet: outcome.teruggezet, nietTeruggezet: outcome.nietTeruggezet })
      }
      opened.provider.on('synced', finish)
    }, delay)
    switching.set(section.id, { oud, provider: undefined, timer })
    wake()
  }

  for (const section of projection.secties) {
    const laag = laagOf(section.type)
    // Een bronsectie hoort bij geen laag en heeft geen document (R-56, 6.8).
    if (laag === null) continue
    const opened = openSection(section, laag)
    providers.push(opened.provider)
    perSection.set(section.id, opened.provider)
    documents.set(section.id, opened.doc)
    connections.set(section.id, opened.verbinding)
  }

  return {
    projectie: projection,
    modus: mode,
    verwijzingen,
    documenten: documents,
    verbindingen: connections,
    identiteit: context.persoon,
    /**
     * Wacht tot alles wat er lokaal staat door de pod is aangenomen.
     *
     * **Op `hasUnsyncedChanges` en niet op `isSynced`, en dat verschil is een fout die hier tot
     * M7.5 stond.** `isSynced` zegt of de eerste handdruk met de server is geweest, en de
     * bibliotheek waarschuwt daar zelf voor: "this does not mean all updates from the client have
     * been persisted to the backend. For this, use `hasUnsyncedChanges`." Na het opkomen staat
     * hij op waar en blijft dat, dus deze functie was daarna een lege belofte. Dat raakte niet
     * alleen de sprong binnen een reeks maar ook het indienen, dat er sinds M5.15 op wacht.
     *
     * Beide voorwaarden en niet één: er moet een handdruk zijn geweest (anders is er niets om op
     * te wachten en zou een verbinding die nog opkomt meteen doorlaten) én er mag niets meer
     * openstaan. `unsyncedChanges` telt af naar nul en zet dan `synced`, dus het `synced`-event
     * is precies het moment waarop een provider klaar is.
     */
    async save() {
      if (expired) throw new SessionExpiredError()
      // Opnieuw zolang er een sectie van provider wisselde terwijl er werd gewacht (eisenboek 12.50): de
      // provider van vóór de naad wordt nooit meer bevestigd, de nieuwe pas na zijn synchronisatie, en wat de
      // pagina daarna terugzet moet er ook zijn.
      for (;;) {
        let switched = false
        const current = [...providers]
        await Promise.all(
          current.filter((provider) => !released.has(provider)).map(
            async (provider) =>
              await new Promise<void>((done, fail) => {
                if (switching.size === 0 && provider.isSynced && !provider.hasUnsyncedChanges) {
                  done()
                  return
                }
                const finish = (): void => {
                  waiting.delete(fail)
                  wakers.delete(onSwitch)
                  provider.off('synced', poll)
                  provider.off('unsyncedChanges', poll)
                  done()
                }
                const poll = (): void => {
                  if (!provider.isSynced || provider.hasUnsyncedChanges) return
                  finish()
                }
                const onSwitch = (): void => {
                  switched = true
                  finish()
                }
                waiting.add(fail)
                wakers.add(onSwitch)
                provider.on('synced', poll)
                provider.on('unsyncedChanges', poll)
              }),
          ),
        )
        if (expired) throw new SessionExpiredError()
        if (!switched && switching.size === 0 && providers.every((provider, index) => provider === current[index])) return
        // Eerst de wissel af laten lopen; de volgende ronde wacht dan op de nieuwe provider.
        if (switching.size > 0) {
          await new Promise<void>((done) => {
            const again = (): void => {
              if (switching.size > 0 && !expired) return
              wakers.delete(again)
              done()
            }
            wakers.add(again)
          })
        }
      }
    },
    expire() {
      if (expired) return
      expired = true
      stopTimers()
      tracker.freeze()
      socket.disconnect()
      for (const fail of waiting) fail(new SessionExpiredError())
      waiting.clear()
      wake()
    },
    release(sectionId, close) {
      const provider = perSection.get(sectionId)
      if (provider === undefined) return
      released.add(provider)
      if (close) provider.destroy()
    },
    unsavedIn(sectionId) {
      return perSection.get(sectionId)?.hasUnsyncedChanges === true
    },
    dispose() {
      // Eén keer, want na het sluiten van een beurt ruimt de pagina zelf op terwijl het effect
      // dezelfde verbinding bij een volgende wissel nog eens opruimt.
      if (left) return
      left = true
      stopTimers()
      for (const pending of switching.values()) clearTimeout(pending.timer)
      switching.clear()
      tracker.stop()
      for (const provider of providers) provider.destroy()
      socket.destroy()
    },
  }
}
